import { useLandlordAuthStore } from '@afinius/hooks/store/useLandlordAuthStore';
import { refreshLandlordToken } from '@afinius/utilities/silent-sso';
import type {
  NavigationGuardReturn,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  RouteRecordRaw,
} from 'vue-router';

const isFailedSsoRedirect = (to: RouteLocationNormalized) =>
  'returnTo' in to.query || 'reason' in to.query;

const restrictAuthenticated = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalizedLoaded,
): Promise<NavigationGuardReturn> => {
  const authStore = useLandlordAuthStore();

  if (!authStore.isAuthenticated) {
    return true;
  }

  if (!isFailedSsoRedirect(to)) {
    return { name: 'tenant.index' };
  }

  if ((await refreshLandlordToken()) === 'expired') {
    authStore.clear();
    return true;
  }

  return { name: 'tenant.index', query: { reason: 'app-open-failed' } };
};

const restrictUnauthenticated = (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalizedLoaded,
): NavigationGuardReturn => {
  const authStore = useLandlordAuthStore();

  if (!authStore.isAuthenticated) {
    return { name: 'auth.login' };
  }

  return true;
};

export const routes: RouteRecordRaw[] = [
  {
    path: 'login',
    name: 'auth.login',
    component: () => import('./views/pages/AuthLoginPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'Inloggen',
    },
  },
  {
    path: 'switch-to-sso',
    name: 'auth.switch-to-sso',
    component: () => import('./views/pages/AuthSwitchToSsoPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'Overschakelen naar Microsoft',
    },
  },
  {
    path: 'register',
    name: 'auth.register',
    component: () => import('./views/pages/AuthRegisterPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'Registreren',
    },
  },
  {
    path: 'email-check',
    name: 'auth.email-check',
    component: () => import('./views/pages/AuthEmailCheckPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'Controleer je inbox',
    },
  },
  {
    path: 'email-verify',
    name: 'auth.email-verify',
    component: () => import('./views/pages/AuthEmailVerifyPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'E-mail verificatie',
    },
  },
  {
    path: 'forgot-password',
    name: 'auth.forgot-password',
    component: () => import('./views/pages/AuthForgotPasswordPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'Wachtwoord vergeten',
    },
  },
  {
    path: 'reset-password',
    name: 'auth.reset-password',
    component: () => import('./views/pages/AuthResetPasswordPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'Wachtwoord aanmaken',
    },
  },
  {
    path: 'logout',
    name: 'auth.logout',
    component: () => import('./views/pages/AuthLogoutPage.vue'),
    beforeEnter: restrictUnauthenticated,
    meta: {
      title: 'Uitloggen',
    },
  },
  {
    path: 'terms',
    name: 'auth.terms',
    component: () => import('./views/pages/AuthTermsPage.vue'),
    beforeEnter: restrictUnauthenticated,
    meta: {
      title: 'Privacybeleid',
      hideTitle: true,
    },
  },
];
